// @flow

/**
 * If and when you update this file, please ensure that the AMD version of it, IntlFormatter.es6
 * is updated with the same set of changes.
 */

const alternateCurrencyFormat = (formattedCurrency: string, locale: string) => {
  switch (locale) {
    case 'pt-BR':
      // Adds space between currency symbol and value.
      return formattedCurrency.replace(/\u0024/, '\u0024\u00A0');
    case 'ru-RU':
      // Replaces rubble currency symbol with abbreviation of the currency name.
      return formattedCurrency.replace(/\u20BD/, '\u0440\u0443\u0431');
    case 'fr-FR':
    case 'de-DE':
      // Most currency symbol with symbol + letters will have symbol proceed letters.  Ex. $US, $CA, etc
      // Samoan Tala is the exception (WS$) there we need to account for case where letters may prefix symbol.
      // Removes space between currency symbol and value.
      return formattedCurrency.replace(/(\u00A0|\u0020)([a-zA-Z]*)([\W])/, '$2$3');
    case 'zh-Hant-TW':
      // Prefixes NT to the dollar symbol
      return formattedCurrency.startsWith('\u0024')
        ? formattedCurrency.replace(/\u0024/, 'NT\u0024')
        : formattedCurrency;
    case 'en-CA':
    case 'fr-CA':
      // Prefixes C to the dollar symbol
      return formattedCurrency.startsWith('\u0024')
        ? formattedCurrency.replace(/\u0024/, 'C\u0024')
        : formattedCurrency;
    case 'en-AU':
      // Prefixes AU to the dollar symbol
      return formattedCurrency.startsWith('\u0024')
        ? formattedCurrency.replace(/\u0024/, 'AU\u0024')
        : formattedCurrency;
    case 'en-SG':
      // Prefixes S to the dollar symbol
      return formattedCurrency.startsWith('\u0024')
        ? formattedCurrency.replace(/\u0024/, 'S\u0024')
        : formattedCurrency;
    default:
      return formattedCurrency;
  }
};

const dateFormatters = new Map();
const numberFormatters = new Map();

export default class IntlFormatter {
  locale: string;
  currency: string;

  constructor(locale: string, currency: string) {
    this.locale = locale;
    this.currency = currency;
  }

  getFormattedNumber(type: string, value: number) {
    const key = `${type};${this.locale}`;
    if (!numberFormatters.has(key)) {
      let options = {};
      switch (type) {
        case 'formatNumberOne':
          options = {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          };
          break;
        case 'formatNumberInteger':
          options = {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          };
          break;
        case 'formatPercentageOne':
          options = {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          };
          break;
        case 'formatPercentageInteger':
          options = {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          };
          break;
        case 'formatPercentageDefault':
          options = {
            style: 'percent',
          };
          break;
        case 'formatCurrency':
          options = {
            style: 'currency',
            currency: this.currency,
          };
          break;
        case 'formatCurrencyInteger':
          options = {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          };
          break;
        case 'formatCurrencyISO':
          options = {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'name',
          };
          break;
        case 'formatCurrencyISOInteger':
          options = {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            currencyDisplay: 'name',
          };
          break;
        default:
      }
      numberFormatters.set(key, new Intl.NumberFormat(this.locale, options));
    }
    return ((numberFormatters.get(key): any): Intl.NumberFormat).format(value);
  }

  formatNumber(type: 'one' | 'integer' | 'default', value: number) {
    switch (type) {
      case 'one':
        return this.getFormattedNumber('formatNumberOne', value);
      case 'integer':
        return this.getFormattedNumber('formatNumberInteger', value);
      default:
        return this.getFormattedNumber('formatNumber', value);
    }
  }

  formatPercentage(type: 'one' | 'integer' | 'default', value: number) {
    switch (type) {
      case 'one':
        return this.getFormattedNumber('formatPercentageOne', value);
      case 'integer':
        return this.getFormattedNumber('formatPercentageInteger', value);
      default:
        return this.getFormattedNumber('formatPercentageDefault', value);
    }
  }

  formatCurrency(value: number) {
    return alternateCurrencyFormat(this.getFormattedNumber('formatCurrency', value), this.locale);
  }

  formatCurrencyInteger(value: number) {
    return alternateCurrencyFormat(
      this.getFormattedNumber('formatCurrencyInteger', value),
      this.locale,
    );
  }

  formatCurrencyISO(value: number) {
    return this.getFormattedNumber('formatCurrencyISO', value);
  }

  formatCurrencyISOInteger(value: number) {
    return this.getFormattedNumber('formatCurrencyISOInteger', value);
  }

  formatDate(type: string, date: Date) {
    // Used cached formatter if available. Formatting a date with a new DateTimeFormat is 99.7% slower than using a cached DateTimeFormat from my benchmarking
    const cacheKey = `${type};${this.locale}`;
    const cachedFormatter = dateFormatters.get(cacheKey);
    if (cachedFormatter) {
      try {
        return cachedFormatter.format(date);
      } catch (e) {
        return DEBUG ? type : '';
      }
    }
    let format = { year: 'numeric', month: 'numeric', day: 'numeric' };
    switch (type) {
      case 'date_short':
        format = { year: 'numeric', month: 'numeric', day: 'numeric' };
        break;
      case 'date_medium':
        format = { month: 'short', day: 'numeric', year: 'numeric' };
        break;
      case 'date_long':
        format = { month: 'long', day: 'numeric', year: 'numeric' };
        break;
      case 'week_date_short':
        format = { month: 'numeric', day: 'numeric', weekday: 'short' };
        break;
      case 'week_date_short_v2':
        format = { month: '2-digit', day: '2-digit', weekday: 'short', year: '2-digit' };
        break;
      case 'week_date_short_v3':
        format = { month: '2-digit', day: '2-digit', weekday: 'short' };
        break;
      case 'week_date_long':
        format = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
        break;
      case 'week_long_date_short_v2':
        format = { weekday: 'long', day: '2-digit', month: '2-digit', year: '2-digit' };
        break;
      case 'date_weekday_long':
        format = { weekday: 'long' };
        break;
      case 'week_long_date_medium_month':
        format = { weekday: 'long', month: 'short', day: 'numeric' };
        break;
      case 'week_date_short_year_long':
        format = { weekday: 'short', month: '2-digit', day: '2-digit', year: 'numeric' };
        break;
      case 'date_short_year_long':
        format = { year: 'numeric', month: '2-digit', day: '2-digit' };
        break;
      case 'date_short_v2':
        format = { year: '2-digit', month: '2-digit', day: '2-digit' };
        break;
      case 'date_medium_month_year':
        format = { month: 'short', year: 'numeric' };
        break;
      case 'date_full_month_year':
        format = { month: 'long', year: 'numeric' };
        break;
      case 'date_medium_month':
        format = { month: 'short', day: 'numeric' };
        break;
      case 'date_full_month':
        format = { month: 'long', day: 'numeric' };
        break;
      case 'time_12_24':
        format = { hour: 'numeric', minute: '2-digit' };
        break;
      case 'date_short_12_24':
        format = {
          month: 'numeric',
          day: '2-digit',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        };
        break;
      case 'date_medium_12_24':
        format = {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        };
        break;
      case 'date_long_12_24':
        format = {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        };
        break;
      case 'week_date_short_12_24':
        format = {
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          weekday: 'short',
        };
        break;
      case 'date_weekday':
        format = { weekday: 'short' };
        break;
      case 'date_short_month':
        format = { month: 'numeric', day: 'numeric' };
        break;
      default:
    }

    const formatter = new Intl.DateTimeFormat(this.locale, format);
    dateFormatters.set(cacheKey, formatter);

    try {
      return formatter.format(date);
    } catch (e) {
      return DEBUG ? type : '';
    }
  }
}
